import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Onboarding.module.css';

interface Option {
  value: string;
  labelKey: string;
}

interface DropdownProps {
  questionTextKey: string;
  options: Option[];
  value: string;
  onChange: (value: string) => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(opt => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    setIsOpen(false);
  };

  return (
    <div className={styles.dropdownContainer} ref={dropdownRef}>
      <button
        className={styles.dropdownSelected}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span 
          className={styles.dropdownSelectedText}
          data-selected={!!selectedOption}
          data-placeholder={t('common_select')}
        >
          {selectedOption ? t(selectedOption.labelKey) : ''}
        </span>
      </button>

      <div className={`${styles.dropdownOptions} ${isOpen ? styles.dropdownOptionsVisible : ''}`}>
        {options.map((option) => (
          <button
            key={option.value}
            className={styles.dropdownOption}
            onClick={() => handleSelect(option.value)}
          >
            <span className={styles.dropdownOptionText}>
              {t(option.labelKey)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default Dropdown; 